import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AppBar, Box, Toolbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PortalHeaderProps {
  businessName?: string;
  logoUrl?: string | null;
  children?: React.ReactNode;
}

export const PortalHeader: React.FC<PortalHeaderProps> = ({
  businessName,
  logoUrl,
  children,
}) => {
  const { t } = useTranslation();
  const portalName = businessName || t('Customer Portal');

  return (
  <AppBar
    position="sticky"
    color="default"
    elevation={0}
    sx={(theme) => ({
      top: { xs: 'max(12px, env(safe-area-inset-top))', sm: 'max(16px, env(safe-area-inset-top))' },
      width: { xs: `calc(100% - ${theme.spacing(4)})`, sm: `calc(100% - ${theme.spacing(6)})` },
      // Match the content inside Container maxWidth="lg", including its gutters.
      maxWidth: `calc(${theme.breakpoints.values.lg}px - ${theme.spacing(6)})`,
      mx: 'auto',
      mt: { xs: 1.5, sm: 2 },
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      boxShadow: 'none',
    })}
  >
    <Toolbar sx={{ minHeight: { xs: 80, sm: 96 }, px: { xs: 2, sm: 3 }, gap: 1.5 }}>
      <Box
        component={RouterLink}
        to="/"
        aria-label={`${portalName} home`}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 1,
          color: 'inherit',
          textDecoration: 'none',
          borderRadius: 1,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 4,
          },
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={portalName}
            sx={{ display: 'block', height: { xs: 56, sm: 72 }, maxWidth: '100%', width: 'auto', objectFit: 'contain', objectPosition: 'left center' }}
          />
        ) : (
          <Typography variant="h6" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
            {portalName}
          </Typography>
        )}
      </Box>
      {children}
    </Toolbar>
  </AppBar>
  );
};
